import FileInput from 'apollo-react/components/FileInput';
import Grid from 'apollo-react/components/Grid';
import Typography from 'apollo-react/components/Typography';
import Upload from 'apollo-react-icons/Upload';
import _ from 'lodash';
import qs from 'query-string';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import HyperLink from './ssHyperLink/HyperLinkComponent';
import { launchPreview, setCourse, uploadScormContent } from '../../actions/ScormActions';
import FileLoading from '../../components/FileLoading';
import { SCORM_CONTENT_TYPE_OPTIONS } from '../../constants/ScormEnums';
import { useFormSelector } from '../../custom-hooks';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { getUITA } from '../../utils/commonUtils';
import { getEnvVar } from '../../utils/env';
import { getDomainUrl } from '../../utils/qs';
import { getCourseNames } from '../selectors/runtime.selectors';

const DEFAULT_LABELS = ['Content (Initial)', 'Content (Refresher)', 'Content (Revision)'];

const SSScormFileInput = ({
  instanceId,
  label: propLabel,
  scormContentTypeOptions: contentTypeOption = 'INITIAL',
  isRequired,
  isViewOnly,
  error,
  formKey,
  isEditor,
  disabled,
}) => {
  const translateString = useTranslation();
  const dispatch = useDispatch();
  const courseNames = useFormSelector(getCourseNames);

  const [uploading, setUploading] = useState(false);
  const [previewLink, setPreviewLink] = useState(null);
  const [fileName, setFileName] = useState('');
  const initialized = useRef(false);

  const certificationType = SCORM_CONTENT_TYPE_OPTIONS[contentTypeOption];
  const courseName = courseNames[certificationType];
  const label = propLabel || DEFAULT_LABELS[certificationType];
  const appUrl = new URL(getEnvVar('REACT_APP_ADMIN_APP_URL'));
  const baseURL = appUrl.origin;

  useEffect(() => {
    const getLaunchPreview = async () => {
      const { itmId } = qs.parse(window.location.search);
      const domainUrl = getDomainUrl();

      setUploading(true);

      if (_.isNil(itmId) || _.isNil(certificationType)) {
        setUploading(false);
        return;
      }

      try {
        const data = await dispatch(
          launchPreview({
            courseId: `CourseId|${itmId}!CertificationType|${certificationType}`,
            RedirectOnExitUrl: `${domainUrl}/close`,
          })
        );

        if (!data.PreviewLink) {
          setPreviewLink(data.PreviewLink);
          setFileName(data.fileName);
        }
      } catch {
        setPreviewLink(null);
      } finally {
        setUploading(false);
      }
    };

    if (!isViewOnly && !isEditor && !initialized.current) {
      getLaunchPreview();
      initialized.current = true;
    }
  }, [isViewOnly, certificationType, dispatch, isEditor]);

  const updateCourseData = (data) => {
    dispatch(setCourse(data.title, data.version, certificationType, formKey));

    setPreviewLink(data.PreviewLink);
    setFileName(data.fileName || fileName);
  };

  const uploadFile = async (file) => {
    const { itmId: CourseId } = qs.parse(window.location.search);
    const domainUrl = getDomainUrl();

    setUploading(true);
    setFileName(file.name);

    const requestData = {
      CourseId,
      CertificationType: certificationType,
      FileName: file.name,
      FileDescription: '',
      FileStream: file,
      ReturnPreviewLink: true,
      RedirectOnExitUrl: `${domainUrl}/close`,
    };

    try {
      const data = await dispatch(uploadScormContent(requestData));
      updateCourseData(data);
    } catch {
      updateCourseData({ title: null, version: 0, PreviewLink: null });
    } finally {
      setUploading(false);
    }
  };

  if (isEditor) {
    return (
      <div style={{ display: 'flex' }}>
        <Typography style={{ marginRight: 15 }}>{label || 'ssScormFileInput'}</Typography>
        <Upload color="primary" />
      </div>
    );
  }

  return (
    <Grid container ui-ta={getUITA('FormCtrl', 'SSScormFileInput')}>
      <Grid item xs={12}>
        <FileInput
          id={instanceId}
          style={{ width: 568 }}
          label={label}
          onChange={uploadFile}
          buttonText={translateString('Browse')}
          placeholder={translateString('File Path')}
          disabled={isEditor || isViewOnly || disabled}
          required={isRequired}
          error={error}
          value={fileName}
        />
      </Grid>
      {uploading && <FileLoading />}
      {!uploading && !isViewOnly && previewLink && (
        <Grid item xs={12}>
          <HyperLink
            pathname={`${baseURL}${previewLink}`}
            text={courseName || ''}
            hasIcon={false}
            target="_blank"
          />
        </Grid>
      )}
    </Grid>
  );
};

export default SSScormFileInput;
